<script setup>
import { onMounted, ref } from 'vue';
import AOS from 'aos';

const freightBenefits = ref([
  {
    titleKey: 'page.bod.op_freight_management.fleet.benefit.0.title',
    descriptionKey: 'page.bod.op_freight_management.fleet.benefit.0.description',
    isFullWidth: true,
  },
  {
    titleKey: 'page.bod.op_freight_management.fleet.benefit.1.title',
    descriptionKey: 'page.bod.op_freight_management.fleet.benefit.1.description',
  },
  {
    titleKey: 'page.bod.op_freight_management.fleet.benefit.2.title',
    descriptionKey: 'page.bod.op_freight_management.fleet.benefit.2.description',
  },
  {
    titleKey: 'page.bod.op_freight_management.fleet.benefit.3.title',
    descriptionKey: 'page.bod.op_freight_management.fleet.benefit.3.description',
  },
  {
    titleKey: 'page.bod.op_freight_management.fleet.benefit.4.title',
    descriptionKey: 'page.bod.op_freight_management.fleet.benefit.4.description',
  },
]);

const fleetBenefits = ref([
  {
    titleKey: 'page.bod.op_fleet_management.fleet.benefits.0.title',
    descriptionKey: 'page.bod.op_fleet_management.fleet.benefits.0.description',
  },
  {
    titleKey: 'page.bod.op_fleet_management.fleet.benefits.1.title',
    descriptionKey: 'page.bod.op_fleet_management.fleet.benefits.1.description',
  },
  {
    titleKey: 'page.bod.op_fleet_management.fleet.benefits.2.title',
    descriptionKey: 'page.bod.op_fleet_management.fleet.benefits.2.description',
  },
  {
    titleKey: 'page.bod.op_fleet_management.fleet.benefits.3.title',
    descriptionKey: 'page.bod.op_fleet_management.fleet.benefits.3.description',
  },
]);

onMounted(() => {
  AOS.init({
    duration: 800,
    once: true,
  });
});
</script>

<template>
  <div class="page-wrapper">
    <div class="hero-section">
      <div class="hero-overlay"></div>
      <h1 class="title" data-aos="fade-up">{{ $t('page.home.title') }}</h1>
    </div>

    <main class="main-content">
      <section class="section-container freight-section" data-aos="fade-up">
        <div class="section-header">
          <h2 class="section-title">{{ $t('page.bod.op_freight_management.title') }}</h2>
          <p class="section-subtitle">{{ $t('page.bod.op_freight_management.OFM') }}</p>
        </div>
        <div class="benefits-grid freight-grid">
          <div
            v-for="(benefit, index) in freightBenefits"
            :key="index"
            class="benefit-card"
            :class="{ 'full-width': benefit.isFullWidth }"
            data-aos="zoom-in-up"
            :data-aos-delay="index * 100"
          >
            <h3>{{ $t(benefit.titleKey) }}</h3>
            <p>{{ $t(benefit.descriptionKey) }}</p>
          </div>
        </div>
      </section>

      <section class="section-container fleet-section">
        <div class="fleet-management-grid">
          <div class="section-header" data-aos="fade-right">
            <h2 class="section-title" style="text-align: left;">
              {{ $t('page.bod.op_fleet_management.title-desccription.title') }}
            </h2>
            <p class="section-subtitle" style="text-align: left; margin-left: 0;">
              {{ $t('page.bod.op_fleet_management.title-desccription.description') }}
            </p>
          </div>
          <div class="benefits-grid fleet-grid" data-aos="fade-left" data-aos-delay="200">
            <div
              v-for="(benefit, index) in fleetBenefits"
              :key="index"
              class="benefit-card"
              :data-aos-delay="index * 100"
            >
              <h3>{{ $t(benefit.titleKey) }}</h3>
              <p>{{ $t(benefit.descriptionKey) }}</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<style scoped>
.hero-section {
  background: url('../assets/rectangle-30@2x.webp') center center no-repeat;
  background-size: cover;
  height: 50vh;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  text-align: center;
}

.hero-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(10, 45, 97, 0.6);
}

.title {
  text-transform: uppercase;
  color: white;
  font-size: 3rem;
  position: relative;
  z-index: 2;
  padding-bottom: 0.5rem;
}

.title::after {
  content: '';
  position: absolute;
  background: #fcbc0d;
  width: 80px;
  height: 4px;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
}

.main-content {
  font-family: 'Montserrat', sans-serif;
  background-color: #fff;
}

.section-container {
  padding: 6rem 5%;
}

.freight-section {
  background: url('../assets/akshay-nanavati-8FevUlxdZC0-unsplash.webp') no-repeat center center;
  background-size: cover;
  position: relative;
}

.freight-section::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(248, 249, 250, 0.85);
  backdrop-filter: blur(5px);
}

.section-header {
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 4rem auto;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 800;
  text-transform: uppercase;
  color: #0A2D61;
  margin-bottom: 1rem;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #555;
  line-height: 1.7;
}

.benefits-grid {
  display: grid;
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.freight-grid {
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
}

.benefit-card {
  background: #ffffff;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 25px rgba(0, 0, 0, 0.07);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.benefit-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 35px rgba(0, 0, 0, 0.1);
}

.full-width {
  grid-column: 1 / -1;
}

.benefit-card h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #0A2D61;
  margin: 0 0 1rem 0;
  text-transform: capitalize;
}

.benefit-card p {
  margin: 0;
  color: #666;
  line-height: 1.6;
}

.fleet-section {
  background-color: #f8f9fa;
}

.fleet-management-grid {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 4rem;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
}

.fleet-section .section-header {
  text-align: left;
  margin: 0;
}

.fleet-section .benefits-grid {
  grid-template-columns: 1fr 1fr;
}

@media (max-width: 992px) {
  .fleet-management-grid {
    grid-template-columns: 1fr;
  }
  .fleet-section .section-header {
    text-align: center;
    margin-bottom: 3rem;
  }
}

@media (max-width: 768px) {
  .title {
    font-size: 2.2rem;
  }
  .section-title {
    font-size: 2rem;
  }
  .freight-grid, .fleet-section .benefits-grid {
    grid-template-columns: 1fr;
  }
}
</style>